import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import SignatureSettings from '../components/broker/SignatureSettings';
import PdfPasswords from '../components/broker/PdfPasswords';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Link as LinkIcon,
  Users,
  Calculator,
  FileText,
  Plus,
  Copy,
  Trash2,
  Eye,
  LogOut,
  Settings,
  Send,
  Clock,
  CheckCircle,
  XCircle,
  Download,
  RefreshCw,
  User,
  Mail,
  Phone,
  ExternalLink,
  Lock,
  Crown,
  TrendingUp,
  Sparkles,
  LayoutGrid
} from 'lucide-react';
import axios from 'axios';
import CreditCalculator from '../components/calculator/CreditCalculator';
import NotaryFeesCalculator from '../components/calculator/NotaryFeesCalculator';
import SalaryCalculator from '../components/calculator/SalaryCalculator';
import DebtToIncomeCalculator from '../components/calculator/DebtToIncomeCalculator';
import BrinxChat from '../components/brinx/BrinxChat';
import CrmModule from '../components/crm/CrmModule';
import { useAuth } from '../context/AuthContext';
import { Landmark, Wallet } from 'lucide-react';
import Logo from '../components/common/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Feature access configuration based on subscription plans
const PLAN_FEATURES = {
  basic: {
    clientLinks: true,
    submissions: true,
    subIntermediaries: true,
    creditCalculator: true,
    notaryCalculator: false,
    salaryCalculator: false,
    debtToIncomeCalculator: false,
    settings: true
  },
  professional: {
    clientLinks: true,
    submissions: true,
    subIntermediaries: true,
    creditCalculator: true,
    notaryCalculator: true,
    salaryCalculator: true,
    debtToIncomeCalculator: true,
    settings: true
  },
  premium: {
    clientLinks: true,
    submissions: true,
    subIntermediaries: true,
    creditCalculator: true,
    notaryCalculator: true,
    salaryCalculator: true,
    debtToIncomeCalculator: true,
    aiAgent: true,
    settings: true
  }
};

const BrokerDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [brokerProfile, setBrokerProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Links state
  const [clientLinks, setClientLinks] = useState([]);
  const [showNewLinkDialog, setShowNewLinkDialog] = useState(false);
  const [newLinkData, setNewLinkData] = useState({ clientName: '', clientEmail: '', clientPhone: '', sendEmail: false });
  const [creatingLink, setCreatingLink] = useState(false);
  const [pdfPassword, setPdfPassword] = useState('');
  const [savingPdfPassword, setSavingPdfPassword] = useState(false);
  
  // Submissions state
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  
  // Sub-intermediari state
  const [subIntermediaries, setSubIntermediaries] = useState([]);
  const [showNewSubDialog, setShowNewSubDialog] = useState(false);
  const [newSubData, setNewSubData] = useState({ name: '', email: '', phone: '', cnp: '' });
  const [creatingSub, setCreatingSub] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  
  // Stats
  const [stats, setStats] = useState({
    totalLinks: 0,
    activeLinks: 0,
    completedForms: 0,
    pendingForms: 0,
    subIntermediariesCount: 0
  });

  // Helper function to check feature access based on subscription plan
  const hasFeatureAccess = (feature) => {
    const plan = brokerProfile?.subscription?.plan || 'basic';
    const planFeatures = PLAN_FEATURES[plan] || PLAN_FEATURES.basic;
    return planFeatures[feature] === true;
  };

  // Get subscription plan name for display
  const getSubscriptionPlanName = () => {
    const plan = brokerProfile?.subscription?.plan || 'basic';
    const names = {
      basic: 'BASIC',
      professional: 'PROFESIONAL',
      premium: 'PREMIUM'
    };
    return names[plan] || 'BASIC';
  };

  const fetchBrokerProfile = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/broker/profile`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBrokerProfile(response.data);
      setPdfPassword(response.data?.pdfPassword || '');
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  }, []);

  const handleSavePdfPassword = async () => {
    setSavingPdfPassword(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/broker/pdf-password`,
        { pdfPassword: pdfPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Parola pentru PDF-uri a fost salvată.');
    } catch (error) {
      console.error('Error saving pdf password:', error);
      alert('Eroare la salvarea parolei.');
    } finally {
      setSavingPdfPassword(false);
    }
  };

  const fetchClientLinks = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/broker/links`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setClientLinks(response.data.links || []);
      
      // Calculate stats
      const links = response.data.links || [];
      setStats(prev => ({
        ...prev,
        totalLinks: links.length,
        activeLinks: links.filter(l => l.status === 'active').length
      }));
    } catch (error) {
      console.error('Error fetching links:', error);
    }
  }, []);

  const fetchSubmissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/broker/submissions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubmissions(response.data.submissions || []);
      
      const subs = response.data.submissions || [];
      setStats(prev => ({
        ...prev,
        completedForms: subs.filter(s => s.status === 'completed').length,
        pendingForms: subs.filter(s => s.status === 'pending').length
      }));
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSubIntermediaries = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/dashboard/sub-intermediaries`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubIntermediaries(response.data.subIntermediaries || []);
      setStats(prev => ({
        ...prev,
        subIntermediariesCount: (response.data.subIntermediaries || []).length
      }));
    } catch (error) {
      console.error('Error fetching sub-intermediaries:', error);
    }
  }, []);

  const createSubIntermediary = async () => {
    if (!newSubData.name || !newSubData.email || !newSubData.phone) return;
    
    setCreatingSub(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/dashboard/sub-intermediaries`,
        newSubData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowNewSubDialog(false);
      setNewSubData({ name: '', email: '', phone: '', cnp: '' });
      fetchSubIntermediaries();
    } catch (error) {
      console.error('Error creating sub-intermediary:', error);
      alert(error.response?.data?.detail || 'Eroare la crearea sub-intermediarului');
    } finally {
      setCreatingSub(false);
    }
  };

  const updateSubIntermediary = async () => {
    if (!editingSub || !newSubData.name || !newSubData.email || !newSubData.phone) return;
    
    setCreatingSub(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/dashboard/sub-intermediaries/${editingSub.id}`,
        newSubData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingSub(null);
      setShowNewSubDialog(false);
      setNewSubData({ name: '', email: '', phone: '', cnp: '' });
      fetchSubIntermediaries();
    } catch (error) {
      console.error('Error updating sub-intermediary:', error);
      alert(error.response?.data?.detail || 'Eroare la actualizarea sub-intermediarului');
    } finally {
      setCreatingSub(false);
    }
  };

  const deleteSubIntermediary = async (subId) => {
    if (!confirm('Sigur doriți să ștergeți acest sub-intermediar?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/api/dashboard/sub-intermediaries/${subId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchSubIntermediaries();
    } catch (error) {
      console.error('Error deleting sub-intermediary:', error);
      alert(error.response?.data?.detail || 'Eroare la ștergerea sub-intermediarului');
    }
  };

  const openEditSubDialog = (sub) => {
    setEditingSub(sub);
    setNewSubData({
      name: sub.name,
      email: sub.email,
      phone: sub.phone,
      cnp: sub.cnp || ''
    });
    setShowNewSubDialog(true);
  };

  useEffect(() => {
    fetchBrokerProfile();
    fetchClientLinks();
    fetchSubmissions();
    fetchSubIntermediaries();
  }, [fetchBrokerProfile, fetchClientLinks, fetchSubmissions, fetchSubIntermediaries]);

  const handleCreateLink = async () => {
    if (!newLinkData.clientName.trim()) {
      alert('Introduceți numele clientului');
      return;
    }
    
    // Validare email dacă se trimite
    if (newLinkData.sendEmail && !newLinkData.clientEmail) {
      alert('Introduceți email-ul clientului pentru a trimite invitația');
      return;
    }
    
    setCreatingLink(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/broker/links`,
        newLinkData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setClientLinks(prev => [response.data.link, ...prev]);
      setShowNewLinkDialog(false);
      setNewLinkData({ clientName: '', clientEmail: '', clientPhone: '', sendEmail: false });
      
      // Copy link to clipboard
      const fullUrl = response.data.link.url || `${window.location.origin}/apply/invite/${response.data.link.id}`;
      navigator.clipboard.writeText(fullUrl);
      
      if (response.data.link.emailSent) {
        alert('✅ Link creat și trimis pe email către client!\n\nLink-ul a fost copiat și în clipboard.');
      } else {
        alert('✅ Link creat și copiat în clipboard!\n\nPuteți trimite link-ul manual pe WhatsApp sau email.');
      }
    } catch (error) {
      console.error('Error creating link:', error);
      alert('Eroare la crearea linkului');
    } finally {
      setCreatingLink(false);
    }
  };

  const handleCopyLink = (linkId) => {
    const fullUrl = `${window.location.origin}/apply/invite/${linkId}`;
    navigator.clipboard.writeText(fullUrl);
    alert('Link copiat!');
  };

  const handleDeleteLink = async (linkId) => {
    if (!window.confirm('Sigur doriți să ștergeți acest link?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/api/broker/links/${linkId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setClientLinks(prev => prev.filter(l => l.id !== linkId));
    } catch (error) {
      console.error('Error deleting link:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Activ', variant: 'default', className: 'bg-green-500' },
      completed: { label: 'Completat', variant: 'default', className: 'bg-blue-500' },
      expired: { label: 'Expirat', variant: 'secondary', className: 'bg-gray-500' },
      pending: { label: 'În așteptare', variant: 'default', className: 'bg-yellow-500' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <Logo size={40} />
              <div>
                <h1 className="text-xl font-bold text-gray-900">FINBRO</h1>
                <p className="text-sm text-gray-500">Dashboard Intermediar</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="font-medium text-gray-900">{brokerProfile?.companyName || 'Intermediar'}</p>
                <p className="text-sm text-gray-500">{user?.email}</p>
              </div>
              {/* Subscription Badge */}
              <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 rounded-full">
                <Crown className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">{getSubscriptionPlanName()}</span>
              </div>
              <Button variant="ghost" size="icon" onClick={handleLogout}>
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={(value) => {
          // Prevent switching to locked tabs
          const featureMap = {
            'notary': 'notaryCalculator',
            'salary': 'salaryCalculator',
            'dti': 'debtToIncomeCalculator',
            'brinx': 'aiAgent'
          };
          const feature = featureMap[value];
          if (feature && !hasFeatureAccess(feature)) {
            return; // Don't switch to locked tab
          }
          setActiveTab(value);
        }}>
          <TabsList className="mb-8 flex-wrap">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <User className="h-4 w-4" /> Prezentare
            </TabsTrigger>
            <TabsTrigger value="links" className="flex items-center gap-2">
              <LinkIcon className="h-4 w-4" /> Linkuri Clienți
            </TabsTrigger>
            <TabsTrigger value="crm" className="flex items-center gap-2" data-testid="crm-tab-trigger">
              <LayoutGrid className="h-4 w-4" /> CRM
            </TabsTrigger>
            <TabsTrigger value="submissions" className="flex items-center gap-2">
              <FileText className="h-4 w-4" /> Cereri Completate
            </TabsTrigger>
            <TabsTrigger value="sub-intermediaries" className="flex items-center gap-2">
              <Users className="h-4 w-4" /> Sub-intermediari
            </TabsTrigger>
            <TabsTrigger value="calculator" className="flex items-center gap-2">
              <Calculator className="h-4 w-4" /> Calculator Credit
            </TabsTrigger>
            <TabsTrigger 
              value="notary" 
              className={`flex items-center gap-2 ${!hasFeatureAccess('notaryCalculator') ? 'opacity-60' : ''}`}
              disabled={!hasFeatureAccess('notaryCalculator')}
            >
              <Landmark className="h-4 w-4" /> Taxe Notariale
              {!hasFeatureAccess('notaryCalculator') && <Lock className="h-3 w-3 text-yellow-600" />}
            </TabsTrigger>
            <TabsTrigger 
              value="salary" 
              className={`flex items-center gap-2 ${!hasFeatureAccess('salaryCalculator') ? 'opacity-60' : ''}`}
              disabled={!hasFeatureAccess('salaryCalculator')}
            >
              <Wallet className="h-4 w-4" /> Calculator Salariu
              {!hasFeatureAccess('salaryCalculator') && <Lock className="h-3 w-3 text-yellow-600" />}
            </TabsTrigger>
            <TabsTrigger 
              value="dti" 
              className={`flex items-center gap-2 ${!hasFeatureAccess('debtToIncomeCalculator') ? 'opacity-60' : ''}`}
              disabled={!hasFeatureAccess('debtToIncomeCalculator')}
            >
              <TrendingUp className="h-4 w-4" /> Grad Îndatorare
              {!hasFeatureAccess('debtToIncomeCalculator') && <Lock className="h-3 w-3 text-yellow-600" />}
            </TabsTrigger>
            <TabsTrigger 
              value="brinx" 
              data-testid="brinx-tab-trigger"
              className={`flex items-center gap-2 ${!hasFeatureAccess('aiAgent') ? 'opacity-60' : ''}`}
              disabled={!hasFeatureAccess('aiAgent')}
            >
              <Sparkles className="h-4 w-4" /> BRINX AI
              {!hasFeatureAccess('aiAgent') && <Lock className="h-3 w-3 text-yellow-600" />}
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="h-4 w-4" /> Setări
            </TabsTrigger>
          </TabsList>

          {/* CRM Tab */}
          <TabsContent value="crm">
            <CrmModule userRole={user?.role} />
          </TabsContent>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Linkuri</p>
                      <p className="text-3xl font-bold">{stats.totalLinks}</p>
                    </div>
                    <LinkIcon className="h-10 w-10 text-blue-500" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Linkuri Active</p>
                      <p className="text-3xl font-bold">{stats.activeLinks}</p>
                    </div>
                    <CheckCircle className="h-10 w-10 text-green-500" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Formulare Completate</p>
                      <p className="text-3xl font-bold">{stats.completedForms}</p>
                    </div>
                    <FileText className="h-10 w-10 text-purple-500" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">În Așteptare</p>
                      <p className="text-3xl font-bold">{stats.pendingForms}</p>
                    </div>
                    <Clock className="h-10 w-10 text-yellow-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Acțiuni Rapide</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Button 
                    onClick={() => { setActiveTab('links'); setShowNewLinkDialog(true); }}
                    className="h-24 flex flex-col items-center justify-center gap-2"
                  >
                    <Plus className="h-8 w-8" />
                    <span>Generează Link Nou</span>
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setActiveTab('calculator')}
                    className="h-24 flex flex-col items-center justify-center gap-2"
                  >
                    <Calculator className="h-8 w-8" />
                    <span>Calculator Credit</span>
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setActiveTab('submissions')}
                    className="h-24 flex flex-col items-center justify-center gap-2"
                  >
                    <FileText className="h-8 w-8" />
                    <span>Vezi Cererile</span>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Public Link */}
            {brokerProfile?.slug && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle>Linkuri pentru clienți</CardTitle>
                  <CardDescription>Trimite clientului linkul potrivit</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div>
                    <p className="text-sm font-medium mb-1">🔵 Link complet (clientul completează tot formularul)</p>
                    <div className="flex items-center gap-4">
                      <Input 
                        value={`${window.location.origin}/apply/${brokerProfile.slug}`}
                        readOnly
                        className="font-mono"
                        data-testid="full-link-input"
                      />
                      <Button onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/apply/${brokerProfile.slug}`);
                        alert('Link complet copiat!');
                      }} data-testid="copy-full-link-btn">
                        <Copy className="h-4 w-4 mr-2" /> Copiază
                      </Button>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium mb-1">🟢 Link scurt (clientul semnează doar Informare, Declarație și GDPR)</p>
                    <p className="text-xs text-gray-500 mb-2">Pentru clienții care nu doresc să completeze formularul. Formularul financiar îl completezi tu în aplicație.</p>
                    <div className="flex items-center gap-4">
                      <Input 
                        value={`${window.location.origin}/apply/${brokerProfile.slug}/semnaturi`}
                        readOnly
                        className="font-mono"
                        data-testid="sign-link-input"
                      />
                      <Button onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/apply/${brokerProfile.slug}/semnaturi`);
                        alert('Link scurt (semnături) copiat!');
                      }} data-testid="copy-sign-link-btn">
                        <Copy className="h-4 w-4 mr-2" /> Copiază
                      </Button>
                    </div>
                    <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-3">
                      <p className="text-xs font-medium text-amber-900 mb-1">🔒 Parolă PDF pentru linkul scurt (Acorduri)</p>
                      <p className="text-xs text-amber-700 mb-2">PDF-ul cu acordurile semnate îți vine pe email criptat cu această parolă. O alegi tu, nu apare niciodată în email.</p>
                      <div className="flex items-center gap-3">
                        <Input
                          type="text"
                          value={pdfPassword}
                          onChange={(e) => setPdfPassword(e.target.value)}
                          placeholder="Ex: ParolaMea2026"
                          className="max-w-xs"
                          data-testid="pdf-password-input"
                        />
                        <Button onClick={handleSavePdfPassword} disabled={savingPdfPassword} data-testid="save-pdf-password-btn">
                          {savingPdfPassword ? 'Se salvează...' : 'Salvează parola'}
                        </Button>
                      </div>
                    </div>
                    <div className="mt-3 bg-blue-50 border border-blue-200 rounded p-3">
                      <p className="text-xs font-medium text-blue-900 mb-1">🔒 Parolă PDF pentru linkul complet (Cerere Credit)</p>
                      <p className="text-xs text-blue-700">Fiecare cerere primește o <strong>parolă aleatoare unică</strong>, generată la trimitere. Din motive de securitate parola <strong>nu apare în email</strong> — o găsiți în tabul <strong>Cereri Completate → Parole PDF</strong>.</p>
                    </div>
                  </div>

                  <div className="pt-2 border-t">
                    <p className="text-sm font-medium mb-2">✍️ Completează formularul în numele clientului</p>
                    <Button
                      onClick={() => window.open(`${window.location.origin}/apply/${brokerProfile.slug}`, '_blank')}
                      variant="outline"
                      data-testid="fill-for-client-btn"
                    >
                      <FileText className="h-4 w-4 mr-2" /> Completează formular pentru client
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Links Tab */}
          <TabsContent value="links">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Linkuri Clienți</CardTitle>
                    <CardDescription>Generează și gestionează linkuri pentru clienți</CardDescription>
                  </div>
                  <Button onClick={() => setShowNewLinkDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" /> Link Nou
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {clientLinks.length === 0 ? (
                  <div className="text-center py-12">
                    <LinkIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">Nu ai creat încă niciun link</p>
                    <Button onClick={() => setShowNewLinkDialog(true)}>
                      <Plus className="h-4 w-4 mr-2" /> Creează Primul Link
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {clientLinks.map(link => (
                      <div key={link.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <span className="font-medium">{link.clientName}</span>
                            {getStatusBadge(link.status)}
                          </div>
                          <div className="text-sm text-gray-500 flex gap-4">
                            {link.clientEmail && <span>📧 {link.clientEmail}</span>}
                            {link.clientPhone && <span>📱 {link.clientPhone}</span>}
                            <span>🕐 Creat: {formatDate(link.createdAt)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleCopyLink(link.id)}>
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" asChild>
                            <a href={`/apply/invite/${link.id}`} target="_blank" rel="noreferrer">
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteLink(link.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Submissions Tab */}
          <TabsContent value="submissions">
            <PdfPasswords />
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Cereri Completate</CardTitle>
                    <CardDescription>Formularele completate de clienți</CardDescription>
                  </div>
                  <Button variant="outline" onClick={fetchSubmissions}>
                    <RefreshCw className="h-4 w-4 mr-2" /> Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {submissions.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Nu există cereri completate încă</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {submissions.map(sub => (
                      <div key={sub.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <span className="font-medium">{sub.clientName || 'Client'}</span>
                            {getStatusBadge(sub.status)}
                          </div>
                          <div className="text-sm text-gray-500">
                            <span>📅 Completat: {formatDate(sub.completedAt)}</span>
                            {sub.creditType && <span className="ml-4">💳 {sub.creditType}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => setSelectedSubmission(sub)}>
                            <Eye className="h-4 w-4 mr-1" /> Vezi
                          </Button>
                          {sub.pdfUrl && (
                            <Button variant="outline" size="sm" asChild>
                              <a href={sub.pdfUrl} download>
                                <Download className="h-4 w-4 mr-1" /> PDF
                              </a>
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sub-intermediari Tab */}
          <TabsContent value="sub-intermediaries">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Sub-intermediari</CardTitle>
                    <CardDescription>Agenții care lucrează sub firma dumneavoastră</CardDescription>
                  </div>
                  <Button onClick={() => {
                    setEditingSub(null);
                    setNewSubData({ name: '', email: '', phone: '', cnp: '' });
                    setShowNewSubDialog(true);
                  }}>
                    <Plus className="h-4 w-4 mr-2" /> Adaugă Sub-intermediar
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {subIntermediaries.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-2">Nu aveți sub-intermediari încă</p>
                    <p className="text-sm text-gray-400">Adăugați agenți care vor lucra sub firma dumneavoastră</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {subIntermediaries.map(sub => (
                      <div key={sub.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">{sub.name}</p>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" /> {sub.email}
                              </span>
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" /> {sub.phone}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={sub.isActive ? 'bg-green-500' : 'bg-gray-500'}>
                            {sub.isActive ? 'Activ' : 'Inactiv'}
                          </Badge>
                          <Button variant="outline" size="sm" onClick={() => openEditSubDialog(sub)}>
                            Editează
                          </Button>
                          <Button variant="outline" size="sm" className="text-red-500 hover:text-red-700" onClick={() => deleteSubIntermediary(sub.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Calculator Tab */}
          <TabsContent value="calculator">
            <CreditCalculator />
          </TabsContent>

          {/* Notary Fees Calculator Tab */}
          <TabsContent value="notary">
            {hasFeatureAccess('notaryCalculator') ? (
              <Card>
                <CardContent className="p-6">
                  <NotaryFeesCalculator />
                </CardContent>
              </Card>
            ) : (
              <Card className="border-2 border-yellow-200 bg-yellow-50">
                <CardContent className="p-8 text-center">
                  <Lock className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Calculator Taxe Notariale</h3>
                  <p className="text-gray-600 mb-4">
                    Această funcționalitate este disponibilă începând cu planul <strong>PROFESIONAL</strong>.
                  </p>
                  <p className="text-sm text-gray-500 mb-6">
                    Planul tău curent: <Badge className="ml-1">{getSubscriptionPlanName()}</Badge>
                  </p>
                  <Button className="bg-blue-600 hover:bg-blue-700" disabled>
                    <Crown className="h-4 w-4 mr-2" />
                    Contactează Administratorul pentru Upgrade
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Salary Calculator Tab */}
          <TabsContent value="salary">
            {hasFeatureAccess('salaryCalculator') ? (
              <Card>
                <CardContent className="p-6">
                  <SalaryCalculator />
                </CardContent>
              </Card>
            ) : (
              <Card className="border-2 border-yellow-200 bg-yellow-50">
                <CardContent className="p-8 text-center">
                  <Lock className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Calculator Salariu</h3>
                  <p className="text-gray-600 mb-4">
                    Această funcționalitate este disponibilă începând cu planul <strong>PROFESIONAL</strong>.
                  </p>
                  <p className="text-sm text-gray-500 mb-6">
                    Planul tău curent: <Badge className="ml-1">{getSubscriptionPlanName()}</Badge>
                  </p>
                  <Button className="bg-blue-600 hover:bg-blue-700" disabled>
                    <Crown className="h-4 w-4 mr-2" />
                    Contactează Administratorul pentru Upgrade
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Debt-to-Income Calculator Tab */}
          <TabsContent value="dti">
            {hasFeatureAccess('debtToIncomeCalculator') ? (
              <Card>
                <CardContent className="p-6">
                  <DebtToIncomeCalculator />
                </CardContent>
              </Card>
            ) : (
              <Card className="border-2 border-yellow-200 bg-yellow-50">
                <CardContent className="p-8 text-center">
                  <Lock className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Calculator Grad de Îndatorare</h3>
                  <p className="text-gray-600 mb-4">
                    Această funcționalitate este disponibilă începând cu planul <strong>PROFESIONAL</strong>.
                  </p>
                  <p className="text-sm text-gray-500 mb-6">
                    Planul tău curent: <Badge className="ml-1">{getSubscriptionPlanName()}</Badge>
                  </p>
                  <Button className="bg-blue-600 hover:bg-blue-700" disabled>
                    <Crown className="h-4 w-4 mr-2" />
                    Contactează Administratorul pentru Upgrade
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* BRINX AI Tab */}
          <TabsContent value="brinx">
            {hasFeatureAccess('aiAgent') ? (
              <BrinxChat />
            ) : (
              <Card className="border-2 border-yellow-200 bg-yellow-50">
                <CardContent className="p-8 text-center">
                  <Lock className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-800 mb-2">BRINX AI</h3>
                  <p className="text-gray-600 mb-4">
                    Asistentul AI este disponibil doar cu planul <strong>PREMIUM</strong>.
                  </p>
                  <p className="text-sm text-gray-500 mb-6">
                    Planul tău curent: <Badge className="ml-1">{getSubscriptionPlanName()}</Badge>
                  </p>
                  <Button className="bg-blue-600 hover:bg-blue-700" disabled>
                    <Crown className="h-4 w-4 mr-2" />
                    Contactează Administratorul pentru Upgrade
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>Setări Cont</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label>Nume Companie</Label>
                    <Input value={brokerProfile?.companyName || ''} disabled className="mt-1" />
                  </div>
                  <div>
                    <Label>CUI</Label>
                    <Input value={brokerProfile?.cui || ''} disabled className="mt-1" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input value={user?.email || ''} disabled className="mt-1" />
                  </div>
                  <div>
                    <Label>Telefon</Label>
                    <Input value={brokerProfile?.phone || ''} disabled className="mt-1" />
                  </div>
                </div>
                <div className="pt-4 border-t">
                  <Button variant="outline">Schimbă Parola</Button>
                </div>
              </CardContent>
            </Card>
            <div className="mt-6">
              <SignatureSettings />
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* New Link Dialog */}
      <Dialog open={showNewLinkDialog} onOpenChange={setShowNewLinkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generează Link Nou</DialogTitle>
            <DialogDescription>
              Creează un link personalizat pentru client
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nume Client *</Label>
              <Input
                value={newLinkData.clientName}
                onChange={(e) => setNewLinkData({...newLinkData, clientName: e.target.value})}
                placeholder="Ion Popescu"
              />
            </div>
            <div>
              <Label>Email Client {newLinkData.sendEmail ? '*' : '(opțional)'}</Label>
              <Input
                type="email"
                value={newLinkData.clientEmail}
                onChange={(e) => setNewLinkData({...newLinkData, clientEmail: e.target.value})}
                placeholder="client@email.com"
              />
            </div>
            <div>
              <Label>Telefon Client (opțional)</Label>
              <Input
                value={newLinkData.clientPhone}
                onChange={(e) => setNewLinkData({...newLinkData, clientPhone: e.target.value})}
                placeholder="0712345678"
              />
            </div>
            
            {/* Opțiune trimitere email */}
            <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <input
                type="checkbox"
                id="sendEmail"
                checked={newLinkData.sendEmail}
                onChange={(e) => setNewLinkData({...newLinkData, sendEmail: e.target.checked})}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="sendEmail" className="flex-1 cursor-pointer">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-gray-900">Trimite link pe email</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Clientul va primi automat un email cu link-ul de completare formular
                </p>
              </label>
            </div>
            
            {!newLinkData.sendEmail && (
              <p className="text-sm text-gray-500 flex items-center gap-2">
                <ExternalLink className="w-4 h-4" />
                Link-ul va fi copiat automat pentru a-l trimite manual pe WhatsApp sau alt canal
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewLinkDialog(false)}>Anulează</Button>
            <Button onClick={handleCreateLink} disabled={creatingLink}>
              {creatingLink ? 'Se creează...' : (newLinkData.sendEmail ? '📧 Creează și Trimite Email' : '📋 Creează și Copiază Link')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submission Detail Dialog */}
      <Dialog open={!!selectedSubmission} onOpenChange={() => setSelectedSubmission(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalii Cerere</DialogTitle>
          </DialogHeader>
          {selectedSubmission && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Client</Label>
                  <p className="font-medium">{selectedSubmission.clientName}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Data Completare</Label>
                  <p className="font-medium">{formatDate(selectedSubmission.completedAt)}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Tip Credit</Label>
                  <p className="font-medium">{selectedSubmission.creditType || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Status</Label>
                  <p>{getStatusBadge(selectedSubmission.status)}</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedSubmission(null)}>Închide</Button>
            {selectedSubmission?.pdfUrl && (
              <Button asChild>
                <a href={selectedSubmission.pdfUrl} download>
                  <Download className="h-4 w-4 mr-2" /> Descarcă PDF
                </a>
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sub-intermediary Dialog */}
      <Dialog open={showNewSubDialog} onOpenChange={(open) => {
        setShowNewSubDialog(open);
        if (!open) {
          setEditingSub(null);
          setNewSubData({ name: '', email: '', phone: '', cnp: '' });
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSub ? 'Editează Sub-intermediar' : 'Adaugă Sub-intermediar'}</DialogTitle>
            <DialogDescription>
              {editingSub ? 'Modifică datele sub-intermediarului' : 'Adaugă un nou agent sub firma dumneavoastră'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nume Complet *</Label>
              <Input
                value={newSubData.name}
                onChange={(e) => setNewSubData({...newSubData, name: e.target.value})}
                placeholder="Ion Popescu"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={newSubData.email}
                onChange={(e) => setNewSubData({...newSubData, email: e.target.value})}
                placeholder="agent@email.com"
              />
            </div>
            <div>
              <Label>Telefon *</Label>
              <Input
                value={newSubData.phone}
                onChange={(e) => setNewSubData({...newSubData, phone: e.target.value})}
                placeholder="0712345678"
              />
            </div>
            <div>
              <Label>CNP (opțional)</Label>
              <Input
                value={newSubData.cnp}
                onChange={(e) => setNewSubData({...newSubData, cnp: e.target.value})}
                placeholder="1234567890123"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewSubDialog(false)}>Anulează</Button>
            <Button 
              onClick={editingSub ? updateSubIntermediary : createSubIntermediary} 
              disabled={creatingSub || !newSubData.name || !newSubData.email || !newSubData.phone}
            >
              {creatingSub ? 'Se salvează...' : (editingSub ? 'Salvează' : 'Adaugă')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrokerDashboard;
